/**
 * Galerie Produit Custom
 * Gère les interactions de la galerie d'images produit
 */

class GalleryCustom {
  constructor(container) {
    this.container = container;
    this.mainImage = container.querySelector('[data-main-image] img');
    this.thumbnails = container.querySelectorAll('[data-thumbnail]');
    this.currentMediaId = this.mainImage?.dataset.mediaId;

    this.init();
  }

  init() {
    if (!this.mainImage || !this.thumbnails.length) return;

    this.setupThumbnailClickHandlers();
    this.setupKeyboardNavigation();
  }

  /**
   * Configure les gestionnaires de clic pour les thumbnails
   */
  setupThumbnailClickHandlers() {
    this.thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleThumbnailClick(thumbnail);
      });
    });
  }

  /**
   * Gère le clic sur un thumbnail
   */
  handleThumbnailClick(thumbnail) {
    const mediaId = thumbnail.dataset.mediaId;

    if (mediaId === this.currentMediaId) return;

    const thumbnailImg = thumbnail.querySelector('img');
    if (!thumbnailImg) return;

    this.updateMainImage(thumbnailImg.src, thumbnailImg.alt, mediaId);
    this.updateActiveThumbnail(thumbnail);
    this.currentMediaId = mediaId;
  }

  /**
   * Met à jour l'image principale avec une transition
   */
  updateMainImage(src, alt, mediaId) {
    // Construire l'URL haute résolution
    let highResSrc = src;
    if (src.includes('width=')) {
      highResSrc = src.replace(/width=\d+/, 'width=1600');
    } else if (/_\d+x\./.test(src)) {
      highResSrc = src.replace(/(_\d+x\.)/g, '_1600x.');
    } else {
      highResSrc = src + (src.includes('?') ? '&' : '?') + 'width=1600';
    }

    this.mainImage.classList.add('is-changing');

    setTimeout(() => {
      this.mainImage.removeAttribute('srcset');
      this.mainImage.src = highResSrc;
      this.mainImage.alt = alt;
      this.mainImage.dataset.mediaId = mediaId;

      this.mainImage.addEventListener('load', () => {
        this.mainImage.classList.remove('is-changing');
        this.mainImage.setAttribute('data-loaded', '');
      }, { once: true });

      this.mainImage.addEventListener('error', () => {
        this.mainImage.classList.remove('is-changing');
      }, { once: true });
    }, 150);
  }

  /**
   * Met à jour l'état actif des thumbnails
   */
  updateActiveThumbnail(activeThumbnail) {
    this.thumbnails.forEach((thumb) => {
      if (thumb === activeThumbnail) {
        thumb.classList.add('gallery-custom__thumbnail--active');
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.classList.remove('gallery-custom__thumbnail--active');
        thumb.removeAttribute('aria-current');
      }
    });
  }

  /**
   * Configure la navigation au clavier
   */
  setupKeyboardNavigation() {
    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('keydown', (e) => {
        let targetIndex = -1;

        switch (e.key) {
          case 'ArrowRight':
            targetIndex = index + 1;
            break;
          case 'ArrowLeft':
            targetIndex = index - 1;
            break;
          case 'Home':
            targetIndex = 0;
            break;
          case 'End':
            targetIndex = this.thumbnails.length - 1;
            break;
          default:
            return;
        }

        e.preventDefault();

        if (targetIndex >= 0 && targetIndex < this.thumbnails.length) {
          this.thumbnails[targetIndex].focus();
          this.handleThumbnailClick(this.thumbnails[targetIndex]);
        }
      });
    });
  }
}

/**
 * Initialisation automatique
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGalleryCustom);
} else {
  initGalleryCustom();
}

function initGalleryCustom() {
  const galleries = document.querySelectorAll('[data-gallery-custom]');
  galleries.forEach((gallery) => {
    new GalleryCustom(gallery);
  });
  initGallerySliders();
}

/**
 * Slider mobile (scroll-snap natif) : construit les points de pagination et
 * suit la slide visible via IntersectionObserver. Aucun swipe custom : le
 * défilement tactile est natif. Desktop : le slider est masqué en CSS, ce code
 * ne fait rien de visible (dots invisibles).
 */
function initGallerySliders() {
  document.querySelectorAll('[data-gallery-slider]').forEach((slider) => {
    const track = slider.querySelector('[data-slides]');
    const slides = Array.prototype.slice.call(slider.querySelectorAll('[data-slide]'));
    const dotsWrap = slider.querySelector('[data-dots]');
    if (!track || slides.length < 2) return;

    // Construire un point par slide (une seule fois)
    if (dotsWrap && !dotsWrap.children.length) {
      slides.forEach((slide, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'gallery-custom__dot';
        dot.setAttribute('aria-label', 'Image ' + (i + 1));
        dot.addEventListener('click', () => {
          slide.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        });
        dotsWrap.appendChild(dot);
      });
    }
    const dots = dotsWrap ? Array.prototype.slice.call(dotsWrap.children) : [];

    const setActive = (index) => {
      dots.forEach((d, di) => d.classList.toggle('is-active', di === index));
    };
    setActive(0);

    // La slide active = celle majoritairement visible dans le track
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = slides.indexOf(entry.target);
            if (idx >= 0) setActive(idx);
          }
        });
      },
      { root: track, threshold: 0.6 }
    );
    slides.forEach((slide) => observer.observe(slide));
  });
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = GalleryCustom;
}
